import { Layout } from "@/components/Layout";
import { Link } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  buildPayoutRows,
  PAYOUT_SOURCES,
  type JackpotAnalysisInput,
  type PayoutFaq,
} from "@/data/jackpot-analysis";

function formatMoney(n: number) {
  if (n >= 1_000_000)
    return `$${(n / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}M`;
  return `$${Math.round(n).toLocaleString()}`;
}

export interface JackpotAnalysisPageProps {
  game: "Powerball" | "Mega Millions";
  input: JackpotAnalysisInput;
  faqs: PayoutFaq[];
  intro: string;
  drawingsText: string;
  oddsText: string;
}

export function JackpotAnalysisPage({
  game,
  input,
  faqs,
  intro,
  drawingsText,
  oddsText,
}: JackpotAnalysisPageProps) {
  const rows = buildPayoutRows(input);
  const cashValue = input.advertisedJackpot * input.cashRatio;
  const gameSlug = game === "Powerball" ? "powerball" : "mega-millions";
  const otherGame = game === "Powerball" ? "Mega Millions" : "Powerball";
  const otherSlug = game === "Powerball" ? "mega-millions" : "powerball";

  return (
    <Layout>
      <article className="container max-w-5xl py-10 space-y-10">
        <header className="space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {game} Jackpot Analysis: State-by-State Payouts
          </h1>
          <p className="text-muted-foreground" data-speakable>
            {intro}
          </p>
        </header>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Advertised Jackpot" value={formatMoney(input.advertisedJackpot)} />
          <Stat label="Cash Value" value={formatMoney(cashValue)} />
          <Stat label="Cash Ratio" value={`${Math.round(input.cashRatio * 100)}%`} />
          <Stat label="Annuity Years" value={`${input.annuityYears ?? 30}`} />
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">How we calculate payouts</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
            <li>Cash value = advertised jackpot × cash ratio published by {game}.</li>
            <li>Federal tax: 37% top marginal bracket (24% withheld at claim; balance owed at filing).</li>
            <li>State tax: top marginal income-tax rate for the state of residence.</li>
            <li>Annual annuity payment = advertised jackpot ÷ {input.annuityYears ?? 30} (simplified — actual payments grow 5% per year).</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Lump-sum vs. annuity by state</h2>
          <p className="text-sm text-muted-foreground">
            Based on a ${(input.advertisedJackpot / 1_000_000).toLocaleString()} million advertised jackpot. Values scale linearly — multiply by the ratio of the current jackpot to ${(input.advertisedJackpot / 1_000_000).toLocaleString()}M.
          </p>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">State Tax</TableHead>
                  <TableHead className="text-right">Lump-Sum Net</TableHead>
                  <TableHead className="text-right">Annual Annuity Net</TableHead>
                  <TableHead className="text-right">30-Yr Annuity Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.abbreviation}>
                    <TableCell className="font-medium">
                      <Link
                        to="/tools/tax-calculator/$stateSlug"
                        params={{ stateSlug: r.slug }}
                        className="hover:underline"
                      >
                        {r.state}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right">{r.stateTaxRate.toFixed(2)}%</TableCell>
                    <TableCell className="text-right">{formatMoney(r.lumpSumNet)}</TableCell>
                    <TableCell className="text-right">{formatMoney(r.annualAnnuityNet)}</TableCell>
                    <TableCell className="text-right">{formatMoney(r.totalAnnuityNet)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Drawings & odds</h2>
          <p className="text-muted-foreground">{drawingsText}</p>
          <p className="text-muted-foreground">{oddsText}</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Frequently asked questions</h2>
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Related {game} pages</h2>
          <p className="text-sm text-muted-foreground">
            Bring these payout estimates back to real drawings and rules:
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            <li className="rounded-md border p-3">
              <Link to="/games/$gameSlug" params={{ gameSlug }} className="font-medium text-primary hover:underline">
                {game} latest jackpot & winning numbers →
              </Link>
              <p className="mt-1 text-sm text-muted-foreground">
                Current advertised jackpot, cash value, and the most recent draw.
              </p>
            </li>
            <li className="rounded-md border p-3">
              <Link to="/games/$gameSlug/history" params={{ gameSlug }} className="font-medium text-primary hover:underline">
                {game} results history →
              </Link>
              <p className="mt-1 text-sm text-muted-foreground">
                Full archive of past winning numbers and multipliers.
              </p>
            </li>
            <li className="rounded-md border p-3">
              <Link to="/results" className="font-medium text-primary hover:underline">
                All lottery results by date →
              </Link>
              <p className="mt-1 text-sm text-muted-foreground">
                Cross-game daily results across every U.S. lottery.
              </p>
            </li>
            <li className="rounded-md border p-3">
              <Link
                to={otherSlug === "powerball" ? "/powerball/jackpot-analysis" : "/mega-millions/jackpot-analysis"}
                className="font-medium text-primary hover:underline"
              >
                {otherGame} payout analysis →
              </Link>
              <p className="mt-1 text-sm text-muted-foreground">
                Compare {otherGame} lump-sum and annuity payouts state by state.
              </p>
            </li>
          </ul>
        </section>

        <section className="space-y-2 text-sm">
          <h2 className="text-xl font-semibold">Sources</h2>
          <p className="text-muted-foreground">
            Want to plug in your own jackpot and filing status? Try the{" "}
            <Link to="/tools/payout-calculator" className="text-primary hover:underline">
              interactive payout calculator
            </Link>
            . See also{" "}
            <Link
              to={game === "Powerball" ? "/powerball/how-to-play" : "/mega-millions/how-to-play"}
              className="text-primary hover:underline"
            >
              how to play {game}
            </Link>{" "}
            and our{" "}
            <Link to="/tools/tax-calculator" className="text-primary hover:underline">
              state-by-state tax calculator
            </Link>
            .
          </p>
          <ul className="list-disc pl-6 space-y-1">
            {PAYOUT_SOURCES.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground pt-2">
            Estimates are for informational purposes only and do not constitute tax or financial advice. Consult a licensed tax professional before claiming a prize.
          </p>
        </section>
      </article>
    </Layout>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-4">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}